import { useCallback } from "react";
import { useFetch } from "../useFetch";
import type {
	FaqResponse,
	CreateFaqPayload,
	UpdateFaqPayload,
} from "../../core/types/services";

// ============================================
// GET ALL FAQs
// ============================================
export function useFaqs() {
	return useFetch<FaqResponse[]>({
		endpoint: "/faq",
		method: "GET",
	});
}

// ============================================
// GET FAQ BY ID
// ============================================
export function useFaq(id: number) {
	return useFetch<FaqResponse>({
		endpoint: `/faq/${id}`,
		method: "GET",
	});
}

// ============================================
// GET FAQs BY PROVIDER
// ============================================
export function useFaqsByProvider(providerId?: number, auto = true) {
	const fetch = useFetch<FaqResponse[]>({
		endpoint: `/faq/provider/${providerId ?? 0}`,
		method: "GET",
		auto: auto && typeof providerId === "number" && providerId > 0,
	});
	const { execute } = fetch;

	const fetchByProvider = useCallback(
		(targetProviderId: number) => {
			return execute({ endpoint: `/faq/provider/${targetProviderId}` });
		},
		[execute],
	);

	return { ...fetch, fetchByProvider };
}

// ============================================
// CREATE FAQ
// ============================================
export function useCreateFaq() {
	const fetch = useFetch<FaqResponse, CreateFaqPayload>({
		endpoint: "/faq",
		method: "POST",
		auto: false,
	});

	const create = (data: CreateFaqPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE FAQ
// ============================================
export function useUpdateFaq(id?: number) {
	const fetch = useFetch<FaqResponse, UpdateFaqPayload>({
		endpoint: `/faq/${id ?? 0}`,
		method: "PATCH",
		auto: false,
	});

	const update = (faqIdOrData: number | UpdateFaqPayload, data?: UpdateFaqPayload) => {
		if (typeof faqIdOrData === "number") {
			if (!data) {
				return Promise.reject(new Error("Debe enviar los datos para actualizar la FAQ"));
			}
			return fetch.execute({
				endpoint: `/faq/${faqIdOrData}`,
				body: data,
			});
		}

		if (!id) {
			return Promise.reject(new Error("Se requiere el id de la FAQ para actualizar"));
		}

		return fetch.execute({
			endpoint: `/faq/${id}`,
			body: faqIdOrData,
		});
	};

	return { ...fetch, update };
}

// ============================================
// DELETE FAQ
// ============================================
export function useDeleteFaq() {
	const fetch = useFetch<{ message: string }>({
		endpoint: "/faq",
		method: "DELETE",
		auto: false,
	});

	const remove = (id: number) => {
		return fetch.execute({ endpoint: `/faq/${id}` });
	};

	return { ...fetch, remove };
}
